import { Button, Card, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { StatTile } from '@/components/StatTile'
import { PageHeader } from '@/app/layouts/PageHeader'

export interface AssetListing {
  readonly id: string
  readonly name: string
  readonly city: string
  readonly kind: string
  readonly yieldRate: string
  readonly funded: number
  readonly minimum: string
  readonly status: 'open' | 'building' | 'closed'
  readonly completes: string
}

export const LISTINGS: readonly AssetListing[] = [
  { id: 'polanco', name: 'Torre Polanco', city: 'Mexico City', kind: 'Residential · 48 units', yieldRate: '9.2%', funded: 0.74, minimum: '$100', status: 'open', completes: 'Built · renting now' },
  { id: 'chapinero', name: 'Chapinero Lofts', city: 'Bogotá', kind: 'Residential · 22 units', yieldRate: '10.4%', funded: 0.38, minimum: '$100', status: 'building', completes: 'Completes Q3 2027' },
  { id: 'pinheiros', name: 'Pinheiros Retail', city: 'São Paulo', kind: 'Retail · 6 units', yieldRate: '8.1%', funded: 0.91, minimum: '$250', status: 'open', completes: 'Built · renting now' },
  { id: 'equipetrol', name: 'Equipetrol Offices', city: 'Santa Cruz', kind: 'Office · 12 floors', yieldRate: '11.0%', funded: 0.16, minimum: '$100', status: 'building', completes: 'Completes Q1 2028' },
]

export function RealAssetsScreen() {
  return (
    <>
      <PageHeader
        title="Real assets"
        subtitle="Fractional property in the places you already send money to. From $100."
        actions={<Button variant="ghost" size="md" iconStart="calculate" to="/real-assets/calculator">Returns calculator</Button>}
      />

      <BentoGrid>
        {[
          { label: 'Your holdings', value: '$3,200.00', note: 'Across 2 buildings' },
          { label: 'Paid out this year', value: '$214.40', tone: 'positive' as const, note: 'Rent, monthly' },
          { label: 'Average yield', value: '9.6%', note: 'Net of fees' },
          { label: 'Platform fee', value: '1.0%', note: 'Of rent collected' },
        ].map((stat) => (
          <StatTile key={stat.label} stat={stat} />
        ))}

        {LISTINGS.map((listing) => (
          <BentoSpan key={listing.id} span={2}>
            <Card tone="light">
              <Card.Header>
                <Stack gap={1}>
                  <Text variant="subtitle" weight="bold">{listing.name}</Text>
                  <Text variant="eyebrow" tone="secondary">{listing.city} · {listing.kind}</Text>
                </Stack>
                <Pill tone={listing.status === 'building' ? 'warning' : 'accent'}>
                  {listing.status === 'building' ? 'Building' : 'Renting'}
                </Pill>
              </Card.Header>

              <Stack direction="row" gap={6} className="pb-mt-5" wrap>
                <Stack gap={1}>
                  <Text variant="eyebrow" tone="secondary">Target yield</Text>
                  <Text variant="heading" weight="bold" numeric>{listing.yieldRate}</Text>
                </Stack>
                <Stack gap={1}>
                  <Text variant="eyebrow" tone="secondary">Minimum</Text>
                  <Text variant="heading" weight="bold" numeric>{listing.minimum}</Text>
                </Stack>
              </Stack>

              <Stack gap={2} className="pb-mt-5">
                <Stack direction="row" justify="between" align="center">
                  <Text variant="eyebrow" tone="secondary">{Math.round(listing.funded * 100)}% funded</Text>
                  <Text variant="eyebrow" tone="secondary">{listing.completes}</Text>
                </Stack>
                <Meter value={listing.funded} height={6} tone="ink" label={`${listing.name} funding`} />
              </Stack>

              <Card.Footer>
                <Button variant="outline" size="sm" block iconEnd="arrow_forward" to={`/real-assets/${listing.id}`}>
                  See the building
                </Button>
              </Card.Footer>
            </Card>
          </BentoSpan>
        ))}

        <BentoSpan span={4}>
          <Card tone="dark">
            <Text variant="caption" weight="semibold" tone="onDark">What you are actually buying</Text>
            <Text variant="caption" tone="onDarkMuted" className="pb-mt-3">
              A tokenised share of the rent and the eventual sale, held through a regulated vehicle
              in the country the building is in. It is illiquid: the secondary market is where you
              sell before completion, and there may be no buyer on the day you want one.
            </Text>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
